import isDeployError from './isDeployError';

// CefSharp's JavaScript bridge emits this rejection while Outlook Safe Links
// and similar security scanners inspect a page. It is injected into the page,
// has no application stack, and cannot be handled by application code. Keep
// the match narrow so a real "object not found" failure is still reported.
const isExternalObjectBridgeError = (value) =>
  /Object Not Found Matching Id:[^,\s]+,\s*MethodName:update,\s*ParamCount:4\b/i.test(
    String(value?.message || value || '')
  );

const EXTENSION_FRAME = /^(?:chrome|moz|safari|safari-web)-extension:\/\//i;
const NETLIFY_RUM_FRAME = /(?:^|\/)\.netlify\/scripts\/rum(?:$|[?#])/i;
const NETLIFY_RUM_ENDPOINT =
  /^https:\/\/ingesteer\.services-prod\.nsvcs\.net\/rum_collection(?:$|[?#])/i;
const FETCH_FAILURE = /\b(?:failed to fetch|load failed|networkerror)\b/i;
const ABORT_WITHOUT_REASON = /^signal is aborted without reason$/i;
const APOLLO_UPLOAD_LINK_FRAME =
  /(?:^|\/)apollo-upload-client\/(?:public\/)?createUploadLink(?:\.m?js)?(?:$|[?#])/i;
const GRAPHQL_ENDPOINT = /\/graphql(?:$|[?#])/i;
const isSentryWrapperFrame = (frame) => frame?.function === 'sentryWrapped';

// Extensions execute inside the page, so the browser's global error handler
// reports their failures to Sentry. Drop an event only when every source-bearing
// frame is extension code. If even one web/application path is present, retain
// it because an extension may have exposed a genuine application failure.
const isExtensionOnlyError = (event) => {
  const frames = (event?.exception?.values || []).flatMap(
    ({ stacktrace }) => stacktrace?.frames || []
  );
  const sourceFrames = frames.filter(
    ({ filename, abs_path: absolutePath }) => filename || absolutePath
  );
  const hasExtensionFrame = sourceFrames.some(
    ({ filename, abs_path: absolutePath }) =>
      EXTENSION_FRAME.test(filename || absolutePath || '')
  );

  return (
    hasExtensionFrame &&
    sourceFrames.every(
      ({ filename, abs_path: absolutePath, ...frame }) =>
        EXTENSION_FRAME.test(filename || absolutePath || '') ||
        isSentryWrapperFrame(frame)
    )
  );
};

// Netlify injects its Real User Monitoring script after our bundle is built.
// When its telemetry collector is blocked or temporarily unreachable, the
// script leaves a rejected fetch promise behind and Sentry attributes it to
// the application. Require both the network error and Netlify-owned evidence
// so failures from our own API requests continue to be reported.
const isNetlifyRumIngestFailure = (event, reportedErrors) => {
  if (!reportedErrors.some((error) => FETCH_FAILURE.test(String(error)))) {
    return false;
  }

  const frames = (event?.exception?.values || []).flatMap(
    ({ stacktrace }) => stacktrace?.frames || []
  );
  const hasRumFrame = frames.some(({ filename, abs_path: absolutePath }) =>
    NETLIFY_RUM_FRAME.test(filename || absolutePath || '')
  );
  const hasRumRequest = (event?.breadcrumbs || []).some(({ data }) =>
    NETLIFY_RUM_ENDPOINT.test(data?.url || '')
  );

  return hasRumFrame || hasRumRequest;
};

// apollo-upload-client aborts its internal fetch controller whenever an
// operation's Observable is cleaned up, including immediately after a
// successful response. Chromium creates this AbortError at controller.abort(),
// and browser instrumentation can surface it as an unhandled rejection even
// though the library's cleanup is intentional. Keep this match tied to the
// dependency frame so genuine AbortErrors from application code are retained.
const isApolloUploadCleanupAbort = (event) => {
  const hasSuccessfulGraphqlRequest = (event?.breadcrumbs || []).some(
    ({ category, data }) =>
      category === 'fetch' &&
      data?.method === 'POST' &&
      data?.status_code >= 200 &&
      data?.status_code < 300 &&
      GRAPHQL_ENDPOINT.test(data?.url || '')
  );

  return (event?.exception?.values || []).some(
    ({ type, value, stacktrace }) =>
      type === 'AbortError' &&
      ABORT_WITHOUT_REASON.test(value || '') &&
      ((stacktrace?.frames || []).some(({ filename, abs_path: absolutePath }) =>
        APOLLO_UPLOAD_LINK_FRAME.test(filename || absolutePath || '')
      ) ||
        hasSuccessfulGraphqlRequest)
  );
};

// Stale chunk failures are recovered by deployReload.js. Reporting the error
// that triggered that recovery creates a Sentry issue even though the browser
// immediately refreshes onto the current deployment.
export default function filterSentryEvent(event, hint) {
  const exceptionMessage = event?.exception?.values
    ?.map(({ type, value }) => [type, value].filter(Boolean).join(': '))
    .join('\n');
  const reportedErrors = [
    hint?.originalException,
    exceptionMessage,
    event?.message
  ].filter(Boolean);

  return isExtensionOnlyError(event) ||
    isNetlifyRumIngestFailure(event, reportedErrors) ||
    isApolloUploadCleanupAbort(event) ||
    reportedErrors.some(
      (error) => isDeployError(error) || isExternalObjectBridgeError(error)
    )
    ? null
    : event;
}
